import type { ApiClient } from '../../../shared/api/api-client';
import { ApiError } from '../../../shared/api/api-error';
import { parseOrgLifecycleStatus } from '../lib/org-lifecycle';
import type {
  PublicOrganizationTypeDto,
  SuperadminAssignPlanBody,
  SuperadminOrganizationCreateBody,
  SuperadminOrganizationListResponse,
  SuperadminOrganizationModuleItemDto,
  SuperadminOrganizationModulesDto,
  SuperadminOrganizationModulesPutBody,
  SuperadminOrgMainAdminChangeBody,
  SuperadminOrgMainAdminDto,
  SuperadminOrgMemberSearchItemDto,
  SuperadminOrgMemberSearchResponse,
  SuperadminOrganizationPatchBody,
  SuperadminOrganizationPlatformRemoveResponse,
  SuperadminOrganizationRowDto,
  SuperadminOrganizationSnapshotDto,
  SuperadminOrgListLifecycleQueryStatus,
  SuperadminOrgPlanStatusDto
} from './organizations.types';

export type SuperadminOrganizationsListParams = {
  limit?: number;
  offset?: number;
  q?: string;
  is_active?: boolean;
  organization_type?: string;
  plan_id?: number;
  without_plan?: boolean;
  plan_limit_issue?: boolean;
  /** Фильтр lifecycle — query `status` (как в backend `listOrgs`). */
  lifecycle_status?: SuperadminOrgListLifecycleQueryStatus;
  signal?: AbortSignal;
};

function buildOrganizationsListQuery(params: Omit<SuperadminOrganizationsListParams, 'signal'>): string {
  const sp = new URLSearchParams();
  const limit = params.limit ?? 100;
  const offset = params.offset ?? 0;
  sp.set('limit', String(limit));
  sp.set('offset', String(offset));
  if (params.q?.trim()) sp.set('q', params.q.trim());
  if (params.is_active === true) sp.set('is_active', 'true');
  if (params.is_active === false) sp.set('is_active', 'false');
  if (params.organization_type?.trim()) sp.set('organization_type', params.organization_type.trim());
  if (params.plan_id != null && Number.isFinite(params.plan_id)) sp.set('plan_id', String(params.plan_id));
  if (params.without_plan === true) sp.set('without_plan', 'true');
  if (params.plan_limit_issue === true) sp.set('plan_limit_issue', 'true');
  if (params.lifecycle_status) {
    sp.set('status', params.lifecycle_status);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function extractSettingsString(settings: unknown, key: string): string | null | undefined {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) return undefined;
  const s = settings as Record<string, unknown>;
  const v = s[key];
  return typeof v === 'string' ? v : v === null ? null : undefined;
}

function parseMainAdmin(raw: unknown): SuperadminOrgMainAdminDto | null | undefined {
  if (raw === undefined) return undefined;
  if (raw === null) return null;
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.user_id !== 'number' || !Number.isFinite(o.user_id)) return null;
  return {
    user_id: o.user_id,
    full_name: typeof o.full_name === 'string' ? o.full_name : '',
    phone: typeof o.phone === 'string' ? o.phone : '',
    email: typeof o.email === 'string' ? o.email : ''
  };
}

/** Нормализация `OrganizationResponse` / элемента списка. */
export function parseSuperadminOrganizationRow(raw: unknown): SuperadminOrganizationRowDto | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'number' || typeof o.name !== 'string' || typeof o.slug !== 'string') return null;
  const status = parseOrgLifecycleStatus(o.status);
  const row: SuperadminOrganizationRowDto = {
    id: o.id,
    name: o.name,
    slug: o.slug,
    status,
    status_label: typeof o.status_label === 'string' ? o.status_label : o.status_label === null ? null : undefined,
    next_action: typeof o.next_action === 'string' ? o.next_action : o.next_action === null ? null : undefined,
    is_network: typeof o.is_network === 'boolean' ? o.is_network : undefined,
    organization_type: typeof o.organization_type === 'string' ? o.organization_type : null,
    is_active: typeof o.is_active === 'boolean' ? o.is_active : undefined,
    plan_id: typeof o.plan_id === 'number' ? o.plan_id : o.plan_id === null ? null : undefined,
    plan_name: typeof o.plan_name === 'string' ? o.plan_name : o.plan_name === null ? null : undefined,
    plan_active: typeof o.plan_active === 'boolean' ? o.plan_active : undefined,
    plan_paid_until: typeof o.plan_paid_until === 'string' ? o.plan_paid_until : o.plan_paid_until === null ? null : undefined,
    plan_limit_issue: typeof o.plan_limit_issue === 'boolean' ? o.plan_limit_issue : undefined,
    in_tariff_limit_grace: typeof o.in_tariff_limit_grace === 'boolean' ? o.in_tariff_limit_grace : undefined,
    limit_grace_until: typeof o.limit_grace_until === 'string' ? o.limit_grace_until : o.limit_grace_until === null ? null : undefined,
    payments_enabled: typeof o.payments_enabled === 'boolean' ? o.payments_enabled : undefined,
    never_paid: typeof o.never_paid === 'boolean' ? o.never_paid : undefined,
    branch_label_plural:
      typeof o.branch_label_plural === 'string' ? o.branch_label_plural : o.branch_label_plural === null ? null : undefined,
    branch_label_singular:
      typeof o.branch_label_singular === 'string'
        ? o.branch_label_singular
        : o.branch_label_singular === null
          ? null
          : undefined,
    phone_gateway_billed_to_organization:
      typeof o.phone_gateway_billed_to_organization === 'boolean' ? o.phone_gateway_billed_to_organization : undefined,
    photo_url: typeof o.photo_url === 'string' ? o.photo_url : o.photo_url === null ? null : undefined,
    brand_logo_url: extractSettingsString(o.settings, 'brand_logo_url'),
    current_employees: typeof o.current_employees === 'number' ? o.current_employees : undefined,
    current_clients: typeof o.current_clients === 'number' ? o.current_clients : undefined,
    current_branches: typeof o.current_branches === 'number' ? o.current_branches : undefined,
    max_employees: typeof o.max_employees === 'number' ? o.max_employees : undefined,
    max_clients: typeof o.max_clients === 'number' ? o.max_clients : undefined,
    max_branches: typeof o.max_branches === 'number' ? o.max_branches : undefined
  };
  const mainAdmin = parseMainAdmin(o.main_admin);
  if (mainAdmin !== undefined) row.main_admin = mainAdmin;
  return row;
}

function normalizeList(raw: unknown): SuperadminOrganizationListResponse {
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const itemsRaw = Array.isArray(obj.items) ? obj.items : [];
  const items = itemsRaw
    .map((x) => parseSuperadminOrganizationRow(x))
    .filter((x): x is SuperadminOrganizationRowDto => x != null);
  const total = typeof obj.total === 'number' && Number.isFinite(obj.total) ? obj.total : items.length;
  return { items, total };
}

function normalizePlanStatus(raw: unknown): SuperadminOrgPlanStatusDto | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw as SuperadminOrgPlanStatusDto;
}

function parseOrganizationModuleItem(raw: unknown): SuperadminOrganizationModuleItemDto | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.module_code !== 'string' || o.module_code.trim() === '') return null;
  const enabled = typeof o.enabled === 'boolean' ? o.enabled : false;
  const cfg = o.config_json;
  const lim = o.limits_json;
  const row: SuperadminOrganizationModuleItemDto = {
    module_code: o.module_code.trim(),
    enabled,
    organization_id: typeof o.organization_id === 'number' ? o.organization_id : undefined,
    state_source: typeof o.state_source === 'string' && o.state_source.trim() !== '' ? o.state_source.trim() : undefined,
    updated_at: typeof o.updated_at === 'string' ? o.updated_at : undefined
  };
  if (cfg && typeof cfg === 'object' && !Array.isArray(cfg)) {
    row.config_json = { ...(cfg as Record<string, unknown>) };
  }
  if (lim && typeof lim === 'object' && !Array.isArray(lim)) {
    row.limits_json = { ...(lim as Record<string, unknown>) };
  }
  return row;
}

function normalizeModules(raw: unknown): SuperadminOrganizationModulesDto | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const organizationId = typeof o.organization_id === 'number' ? o.organization_id : null;
  const em = o.effective_modules;
  if (organizationId == null || !em || typeof em !== 'object' || Array.isArray(em)) return null;
  const itemsRaw = Array.isArray(o.items) ? o.items : [];
  const items = itemsRaw
    .map((x) => parseOrganizationModuleItem(x))
    .filter((x): x is SuperadminOrganizationModuleItemDto => x != null);
  return {
    organization_id: organizationId,
    effective_modules: em as Record<string, boolean>,
    items
  };
}

/** GET /api/v1/public/organization-types */
export async function fetchPublicOrganizationTypes(
  client: ApiClient,
  options?: { signal?: AbortSignal }
): Promise<PublicOrganizationTypeDto[]> {
  const raw = await client.request<unknown>('/public/organization-types', {
    method: 'GET',
    impersonateOrganizationId: null,
    signal: options?.signal
  });
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is PublicOrganizationTypeDto => {
    if (!x || typeof x !== 'object') return false;
    const o = x as Record<string, unknown>;
    return typeof o.code === 'string' && typeof o.label === 'string';
  });
}

/** GET /api/v1/superadmin/organizations (глобальный список; без заголовка имперсонации). */
export async function fetchSuperadminOrganizations(
  client: ApiClient,
  params: SuperadminOrganizationsListParams = {}
): Promise<SuperadminOrganizationListResponse> {
  const { signal, ...rest } = params;
  const q = buildOrganizationsListQuery({
    limit: rest.limit ?? 100,
    offset: rest.offset ?? 0,
    q: rest.q,
    is_active: rest.is_active,
    organization_type: rest.organization_type,
    plan_id: rest.plan_id,
    without_plan: rest.without_plan,
    plan_limit_issue: rest.plan_limit_issue,
    lifecycle_status: rest.lifecycle_status
  });
  const raw = await client.request<unknown>(`/superadmin/organizations${q}`, {
    method: 'GET',
    impersonateOrganizationId: null,
    signal
  });
  return normalizeList(raw);
}

/** GET /api/v1/superadmin/organizations/{org_id} — снимок строки (как в list); 404 → `null`. */
export async function fetchSuperadminOrganizationById(
  client: ApiClient,
  organizationId: number,
  options?: { signal?: AbortSignal }
): Promise<SuperadminOrganizationSnapshotDto | null> {
  try {
    const raw = await client.request<unknown>(`/superadmin/organizations/${organizationId}`, {
      method: 'GET',
      impersonateOrganizationId: null,
      signal: options?.signal
    });
    return parseSuperadminOrganizationRow(raw);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/** POST /api/v1/superadmin/organizations — ответ 201 с `OrganizationResponse`. */
export async function postSuperadminOrganization(
  client: ApiClient,
  body: SuperadminOrganizationCreateBody,
  options?: { signal?: AbortSignal }
): Promise<SuperadminOrganizationRowDto> {
  const raw = await client.request<unknown>('/superadmin/organizations', {
    method: 'POST',
    body,
    impersonateOrganizationId: null,
    signal: options?.signal
  });
  const row = parseSuperadminOrganizationRow(raw);
  if (!row) {
    throw new ApiError(500, 'Сервер вернул некорректный ответ при создании организации');
  }
  return row;
}

/** PATCH /api/v1/superadmin/organizations/{org_id} — ответ 200 с телом или пусто. */
export async function patchSuperadminOrganization(
  client: ApiClient,
  organizationId: number,
  body: SuperadminOrganizationPatchBody,
  options?: { signal?: AbortSignal }
): Promise<SuperadminOrganizationRowDto | void> {
  const raw = await client.request<unknown>(`/superadmin/organizations/${organizationId}`, {
    method: 'PATCH',
    body,
    impersonateOrganizationId: null,
    signal: options?.signal
  });
  const row = parseSuperadminOrganizationRow(raw);
  return row ?? undefined;
}

/** GET /api/v1/superadmin/organizations/{org_id}/plan-status */
export async function fetchSuperadminOrganizationPlanStatus(
  client: ApiClient,
  organizationId: number,
  options?: { signal?: AbortSignal }
): Promise<SuperadminOrgPlanStatusDto | null> {
  const raw = await client.request<unknown>(`/superadmin/organizations/${organizationId}/plan-status`, {
    method: 'GET',
    impersonateOrganizationId: null,
    signal: options?.signal
  });
  return normalizePlanStatus(raw);
}

/** POST /api/v1/superadmin/organizations/{org_id}/assign-plan — успех: 200/204 без тела (ядро отдаёт 204). */
export async function postSuperadminOrganizationAssignPlan(
  client: ApiClient,
  organizationId: number,
  body: SuperadminAssignPlanBody,
  options?: { signal?: AbortSignal }
): Promise<void> {
  await client.request<unknown>(`/superadmin/organizations/${organizationId}/assign-plan`, {
    method: 'POST',
    body,
    impersonateOrganizationId: null,
    signal: options?.signal
  });
}

/** POST /api/v1/superadmin/organizations/{org_id}/reset-vip-tariff — только VIP (`vip_no_payment`); успех 204. */
export async function postSuperadminOrganizationResetVipTariff(
  client: ApiClient,
  organizationId: number,
  options?: { signal?: AbortSignal }
): Promise<void> {
  await client.request<unknown>(`/superadmin/organizations/${organizationId}/reset-vip-tariff`, {
    method: 'POST',
    body: {},
    impersonateOrganizationId: null,
    signal: options?.signal
  });
}

/** PUT /api/v1/superadmin/organizations/{org_id}/custom-tariff — задать/сбросить кастомный тариф. */
export async function putSuperadminOrganizationCustomTariff(
  client: ApiClient,
  organizationId: number,
  body: {
    tariff_source: 'plan' | 'custom';
    max_employees?: number | null;
    max_clients?: number | null;
    max_branches?: number | null;
    plan_paid_until?: string | null;
  },
  options?: { signal?: AbortSignal }
): Promise<SuperadminOrganizationSnapshotDto> {
  const raw = await client.request<unknown>(`/superadmin/organizations/${organizationId}/custom-tariff`, {
    method: 'PUT',
    body,
    impersonateOrganizationId: null,
    signal: options?.signal
  });
  return raw as SuperadminOrganizationSnapshotDto;
}

/** POST /api/v1/superadmin/organizations/{org_id}/platform-remove — soft, подтверждение slug в теле. */
export async function postSuperadminOrganizationPlatformRemove(
  client: ApiClient,
  organizationId: number,
  body: { slug: string },
  options?: { signal?: AbortSignal }
): Promise<SuperadminOrganizationPlatformRemoveResponse> {
  const raw = await client.request<unknown>(`/superadmin/organizations/${organizationId}/platform-remove`, {
    method: 'POST',
    body,
    impersonateOrganizationId: null,
    signal: options?.signal
  });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new ApiError(500, 'Сервер вернул некорректный ответ при снятии организации с платформы');
  }
  const o = raw as Record<string, unknown>;
  const ts = o.platform_removed_at;
  if (typeof ts !== 'string' || ts.trim() === '') {
    throw new ApiError(500, 'Сервер вернул некорректный ответ при снятии организации с платформы');
  }
  return { platform_removed_at: ts };
}

/** GET /api/v1/superadmin/organizations/{org_id}/modules */
export async function fetchSuperadminOrganizationModules(
  client: ApiClient,
  organizationId: number,
  options?: { signal?: AbortSignal }
): Promise<SuperadminOrganizationModulesDto | null> {
  const raw = await client.request<unknown>(`/superadmin/organizations/${organizationId}/modules`, {
    method: 'GET',
    impersonateOrganizationId: null,
    signal: options?.signal
  });
  return normalizeModules(raw);
}

/** PUT /api/v1/superadmin/organizations/{org_id}/modules — полная замена `organization_modules`; успех: 204 без тела. */
export async function putSuperadminOrganizationModules(
  client: ApiClient,
  organizationId: number,
  body: SuperadminOrganizationModulesPutBody,
  options?: { signal?: AbortSignal }
): Promise<void> {
  await client.request<unknown>(`/superadmin/organizations/${organizationId}/modules`, {
    method: 'PUT',
    body,
    impersonateOrganizationId: null,
    signal: options?.signal
  });
}

/** GET /api/v1/superadmin/users с фильтром по организации — кандидаты в главные админы студии. */
export async function searchSuperadminOrganizationMembers(
  client: ApiClient,
  organizationId: number,
  params: { q?: string; limit?: number; offset?: number; signal?: AbortSignal } = {}
): Promise<SuperadminOrgMemberSearchResponse> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(organizationId));
  sp.set('limit', String(params.limit ?? 20));
  sp.set('offset', String(params.offset ?? 0));
  if (params.q?.trim()) sp.set('q', params.q.trim());
  const raw = await client.request<unknown>(`/superadmin/users?${sp.toString()}`, {
    method: 'GET',
    impersonateOrganizationId: null,
    signal: params.signal
  });
  const obj = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const itemsRaw = Array.isArray(obj.items) ? obj.items : [];
  const items: SuperadminOrgMemberSearchItemDto[] = [];
  for (const it of itemsRaw) {
    if (!it || typeof it !== 'object' || Array.isArray(it)) continue;
    const o = it as Record<string, unknown>;
    if (typeof o.id !== 'number' || !Number.isFinite(o.id)) continue;
    items.push({
      id: o.id,
      email: typeof o.email === 'string' ? o.email : '',
      phone: typeof o.phone === 'string' ? o.phone : '',
      full_name: typeof o.full_name === 'string' ? o.full_name : '',
      role: typeof o.role === 'string' ? o.role : '',
      is_active: typeof o.is_active === 'boolean' ? o.is_active : true
    });
  }
  const total = typeof obj.total === 'number' && Number.isFinite(obj.total) ? obj.total : items.length;
  return { items, total };
}

/** POST /api/v1/superadmin/organizations/{org_id}/main-admin — единственный путь смены org_main_admin; успех 204. */
export async function changeSuperadminOrganizationMainAdmin(
  client: ApiClient,
  organizationId: number,
  body: SuperadminOrgMainAdminChangeBody,
  options?: { signal?: AbortSignal }
): Promise<void> {
  await client.request<unknown>(`/superadmin/organizations/${organizationId}/main-admin`, {
    method: 'POST',
    body,
    impersonateOrganizationId: null,
    signal: options?.signal
  });
}

/** Объединить снимок из навигации с данными из списка (приоритет у полей из списка). */
export function mergeSuperadminOrganizationSnapshots(
  fromNavigation: SuperadminOrganizationSnapshotDto | null | undefined,
  fromList: SuperadminOrganizationSnapshotDto | null
): SuperadminOrganizationSnapshotDto | null {
  if (!fromNavigation && !fromList) return null;
  if (!fromNavigation) return fromList;
  if (!fromList) return fromNavigation;
  return { ...fromNavigation, ...fromList };
}
